
import { getSocket } from '../ChatPage/socket';

let sequence = 0;
const latestSequenceBySemanticKey = new Map();

const resolvePageKey = () => {
    if (typeof window === 'undefined') return '';
    const pathname = String(window.location.pathname || '');
    if (pathname.startsWith('/admin/catalog-review')) return 'catalog-review';
    if (pathname.startsWith('/containerProductPage/product-seo-price')) return 'product-seo-price';
    return '';
};

const cleanKey = (value) => String(value || '')
    .trim()
    .replace(/[^a-z0-9:._|@/\-]/gi, '-')
    .slice(0, 150);

export const publishAdminRealtimeSemanticState = ({
    namespace,
    stateKey,
    value,
} = {}) => {
    const pageKey = resolvePageKey();
    const safeNamespace = cleanKey(namespace);
    const safeStateKey = cleanKey(stateKey);
    if (!pageKey || !safeNamespace || !safeStateKey) return false;

    const socket = getSocket();
    if (!socket?.connected) return false;

    sequence += 1;
    const eventSequence = sequence;
    const semanticKey = `${pageKey}:${safeNamespace}:${safeStateKey}`;
    latestSequenceBySemanticKey.set(semanticKey, eventSequence);

    const payload = {
        token: window.localStorage.getItem('token'),
        pageKey,
        type: 'state',
        targetId: `semantic:${safeNamespace}:${safeStateKey}`,
        sequence: eventSequence,
        state: {
            namespace: safeNamespace,
            stateKey: safeStateKey,
            value,
        },
    };

    const send = (attempt = 0) => {
        if (!socket?.connected) return;
        if (latestSequenceBySemanticKey.get(semanticKey) !== eventSequence) return;

        socket.emit('adminRealtime:state', payload, (response = {}) => {
            if (response?.ok) return;
            if (response?.code !== 'ADMIN_REALTIME_NOT_JOINED' || attempt >= 2) return;
            window.setTimeout(() => send(attempt + 1), 180 + attempt * 120);
        });
    };

    send();
    return true;
};

export const subscribeAdminRealtimeSemanticState = ({
    namespace,
    onState,
    requestSnapshot = true,
} = {}) => {
    const safeNamespace = cleanKey(namespace);
    const socket = getSocket();
    if (!safeNamespace || !socket || typeof onState !== 'function') return () => {};

    const accept = (payload = {}) => {
        if (payload?.type !== 'state') return;
        if (String(payload?.state?.namespace || '') !== safeNamespace) return;

        const stateKey = String(payload?.state?.stateKey || '');
        if (!stateKey) return;

        const revision = Math.max(0, Number(payload.stateRevision || 0));

        onState({
            stateKey,
            value: payload?.state?.value,
            revision,
            socketId: payload?.socketId || '',
            payload,
        });
    };

    const acceptSnapshot = (snapshot = {}) => {
        if (!Array.isArray(snapshot?.states)) return;
        snapshot.states.forEach(accept);
    };

    socket.on('adminRealtime:state', accept);
    socket.on('adminRealtime:snapshot', acceptSnapshot);

    if (requestSnapshot) {
        const pageKey = resolvePageKey();
        if (pageKey && socket.connected) {
            socket.emit('adminRealtime:resync', {
                token: window.localStorage.getItem('token'),
                pageKey,
            });
        }
    }

    return () => {
        socket.off('adminRealtime:state', accept);
        socket.off('adminRealtime:snapshot', acceptSnapshot);
    };
};

export const createAdminRealtimeSemanticSetter = ({
    namespace,
    stateKey,
    setValue,
} = {}) => (nextValue) => {
    if (typeof setValue !== 'function') return;

    setValue((previous) => {
        const resolved = typeof nextValue === 'function'
            ? nextValue(previous)
            : nextValue;

        publishAdminRealtimeSemanticState({
            namespace,
            stateKey,
            value: resolved,
        });
        return resolved;
    });
};
